import React, { FC, memo, ReactNode } from 'react';
import { Radio, RadioChangeEvent } from 'antd';

import PolicySetting, { PolicyRowProps } from '../PolicySetting';
import {
    detectConflictOnDropdownChange,
    getSettingsToDisable,
} from '../../../../../../stores/PolicyStore/helpers/incompatibleSettings';
import { showConflictModal } from '../../../ConflictModal';
import { PolicySetting as PolicySettingType } from '../../../../../../types/types';
import styles from './PolicyRadioRow.module.scss';

type PolicyRadioRowProps = PolicyRowProps<string> & {
    options: Array<{ label: string; value: string }>;
    nestedContent?: ReactNode;
    allSettings?: PolicySettingType[];
};

const PolicyRadioRow: FC<PolicyRadioRowProps> = ({
    settingValue,
    settingId,
    handleChange,
    isChild,
    options,
    nestedContent,
    allSettings = [],
}) => {
    const onChange = (e: RadioChangeEvent): void => {
        const newValue = e.target.value;

        // Check if this dropdown change would cause conflicts (generic for any squashed dropdown)
        if (allSettings.length > 0) {
            const conflict = detectConflictOnDropdownChange(settingId, newValue, allSettings);

            if (conflict) {
                const settingsToDisable = getSettingsToDisable(conflict);

                showConflictModal(
                    conflict.message,
                    () => handleChange(settingId, newValue, settingsToDisable),
                    () => {},
                );
                return;
            }
        }

        handleChange(settingId, newValue);
    };

    return (
        <PolicySetting
            isChild={isChild}
            settingId={settingId}
            type="radio"
            hideDescription
        >
            <Radio.Group data-testid={`policyEditor_radioGroup_${settingId}`} onChange={onChange} value={settingValue} className={styles['radio-group']}>
                {options.map(({ label, value }) => (
                    <div key={value} className={styles['radio-option']}>
                        <Radio data-testid={`policyEditor_radioOption_${settingId}_${value}`} value={value}>{label}</Radio>
                        {nestedContent && settingValue === value && (
                            <div className={styles['nested-content']}>
                                {nestedContent}
                            </div>
                        )}
                    </div>
                ))}
            </Radio.Group>
        </PolicySetting>
    );
};

export default memo(PolicyRadioRow);
